import type { Role } from "@/types/api";

/** Base path each role's app routes hang off (vessel is root-anchored). */
export function roleBase(role: Role | undefined): string {
  switch (role) {
    case "super_admin": return "/super_admin";
    case "admin":       return "/admin";
    case "shore_staff": return "/shore_staff";
    case "vessel":      return "";
    default:            return "";
  }
}

/** Build an inspection deep link relative to the current user's role. */
export function inspectionPath(role: Role | undefined, ...rest: (string | number)[]): string {
  const base = roleBase(role);
  const tail = rest.length ? `/${rest.join("/")}` : "";
  return `${base}/manage-inspections${tail}`;
}

/** PIQ list path for the current role. */
export function piqPath(role: Role | undefined): string {
  return `${roleBase(role)}/pre-inspections`;
}

/**
 * Who may CREATE inspections / PIQs. Limited to company admins and the
 * vessel Master; everyone else — super_admin, shore_staff, and non-Master
 * vessel crew — is excluded, so the "New inspection" / "New PIQ" buttons are
 * hidden for them. (Master is matched on the free-text rank, slugified the
 * same way the backend does.)
 */
export function canCreateInspections(
  role: Role | undefined,
  rank: string | null | undefined,
): boolean {
  if (role === "admin") return true;
  if (role === "vessel") {
    const slug = (rank ?? "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return slug === "master";
  }
  return false;
}

/** Build a PIQ inspection deep link under the /pre-inspections prefix so
 *  the sidebar highlights the PIQ tab, not the Inspections tab. */
export function piqInspectionPath(role: Role | undefined, inspectionId: number | string, ...rest: string[]): string {
  const base = roleBase(role);
  const tail = rest.length ? `/${rest.join("/")}` : "";
  return `${base}/pre-inspections/${inspectionId}${tail}`;
}
